import React, {
  useContext, useCallback, useRef,
} from 'react';
import dayjs from 'dayjs';
import {
  Animated, TouchableOpacity, Text, Platform, StyleSheet, View,
} from 'react-native';

import FlatList from './WeekScroller.FlatList';
import useDimensions from '../hooks/dimensions';
import { useColors } from '../contexts/theme';
import WeightDot from './WeightDot';
import DayPagingContext from '../contexts/DayPagingContext';


const styles = StyleSheet.create({
  flatList: { height: 70 },
  container: {
    height: 70,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999999,
  },
  dateText: {
    fontSize: 18,
  },
});

const keyExtractor = (item) => item.valueOf().toString();

const WeekScroller = () => {
  const colors = useColors(),
        {
          selectedDate, setSelectedDate, days, dataPerDay, setWeekIndex, weekIndex,
        } = useContext(DayPagingContext),
        today = dayjs().startOf('day'),
        { width } = useDimensions('window'),
        ref = useRef(null),
        itemWidth = width / 9,
        onScroll = useCallback(({ nativeEvent }) => {
          setWeekIndex(Math.round(9 / 7 * nativeEvent.contentOffset.x / width));
        }, [setWeekIndex, width]),
        renderItem = useCallback(({ item }) => {
          const isEnabled = item.valueOf() >= today.valueOf(),
                isSelected = selectedDate.valueOf() === item.valueOf(),
                isToday = item.valueOf() === today.valueOf(),
                itemsOnDay = (dataPerDay[item.valueOf()] || []).length,
                foregroundColor = isSelected ? colors.separator : colors.text,
                backgroundColor = isSelected ? colors.text : colors.separator,
                fontWeight = isToday ? 'bold' : 'normal',
                opacity = isEnabled ? 1 : 0.5;

          return (
            <TouchableOpacity
              onPress={isEnabled ? () => setSelectedDate(item) : null}
              activeOpacity={isEnabled ? 0.2 : 1}
            >
              <Animated.View style={[
                styles.container,
                {
                  width: itemWidth,
                  opacity,
                  backgroundColor,
                }]}
              >
                <Text style={[styles.dateText, { color: foregroundColor, fontWeight }]}>{ item.format('D') }</Text>
                <Text style={{ color: foregroundColor, fontWeight }}>{ item.format('ddd') }</Text>
                <WeightDot weight={itemsOnDay} color={foregroundColor} />
              </Animated.View>
            </TouchableOpacity>
          );
        }, [colors.separator, colors.text, dataPerDay, itemWidth, selectedDate, setSelectedDate, today]),
        scrollForward = useCallback(() => {
          setWeekIndex((index) => {
            const nextWeekIndex = index + 1;
            ref.current.scrollToOffset({ offset: nextWeekIndex * width * 7 / 9 });
            return nextWeekIndex;
          });
        }, [setWeekIndex, width]),
        scrollBack = useCallback(() => {
          setWeekIndex((index) => {
            const previousWeekIndex = index - 1;
            ref.current.scrollToOffset({ offset: previousWeekIndex * width * 7 / 9 });
            return previousWeekIndex;
          });
        }, [setWeekIndex, width]);

  return (
    <View style={{ flexDirection: 'row' }}>
      <TouchableOpacity onPress={weekIndex > 0 ? scrollBack : null}>
        <View style={{
          backgroundColor: colors.surface, width: itemWidth, justifyContent: 'center', alignItems: 'center', flex: 1,
        }}
        >
          <Text style={{ color: colors.text }}>{'<'}</Text>
        </View>
      </TouchableOpacity>
      <FlatList
        data={days}
        ref={ref}
        snapToInterval={width * 7 / 9}
        contentContainerStyle={{ }}
        style={[
          styles.flatList,
          {
            backgroundColor: colors.separator,
          }]}
        keyExtractor={keyExtractor}
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        horizontal
        onScroll={onScroll}
        scrollEventThrottle={0}
        decelerationRate={Platform.OS === 'ios' ? 0.9 : 0.8}
        renderItem={renderItem}
      />
      <TouchableOpacity onPress={scrollForward}>
        <View style={{
          backgroundColor: colors.surface, width: itemWidth, justifyContent: 'center', alignItems: 'center', flex: 1,
        }}
        >
          <Text style={{ color: colors.text }}>{'>'}</Text>
        </View>
      </TouchableOpacity>
    </View>
  );
};

WeekScroller.displayName = 'WeekScroller';

export default WeekScroller;
